import { useState, useEffect } from 'react'

import { useLazyQuery } from '@apollo/client'
import { ActionIcon, Collapse, Loader, Tooltip } from '@mantine/core'
import { Check, X } from 'lucide-react'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DateInput from 'src/components/Inputs/DateInput/DateInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_JOB_LISTINGS } from 'src/graphql/joblistings.graphql'
import { JobStatus } from 'src/graphql/types/jobListings'
import { LinkTypeDisplay } from 'src/graphql/types/trackingLinks'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconEdit from 'src/icons/IconEdit'
import IconInfoCircle from 'src/icons/IconInfoCircle'

import SelectInput from '../SelectInput/SelectInput'

const JOB_LISTINGS_PAGE_LIMIT = 300

const JobInformationCard = ({
  values: appValues = [],
  handleUpdateApplication,
  initialOpened = true,
}) => {
  const [values, setValues] = useState({ ...appValues[0] })
  const [editing, setEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [opened, setOpened] = useState(initialOpened)

  const [
    getJobListings,
    { data: jobListingsResponse, loading: jobListingsLoading },
  ] = useLazyQuery(GET_JOB_LISTINGS, {
    variables: {
      pageInfo: { offset: 0, limit: JOB_LISTINGS_PAGE_LIMIT },
      filters: {
        statuses: [JobStatus.ACTIVE],
      },
    },
    onError: () => {
      toast('Unable to fetch job listings, please try again later.', 'error')
    },
  })

  const jobListingOptions =
    jobListingsResponse?.jobListings.items?.map((jl) => ({
      value: jl.jobListingId,
      label: jl.title,
    })) || []

  const calculateTrackingLinks = (jobListingId) => {
    const jobListing = jobListingsResponse?.jobListings?.items.find(
      (jl) => jl.jobListingId === jobListingId
    )
    return jobListing?.trackingLinks.map((link) => ({
      value: link.trackingLinkId,
      label: `${LinkTypeDisplay[link.linkType]}${
        link?.linkName ? `: ${link.linkName}` : ``
      }`,
    }))
  }

  // Load job listings on mount if a job listing is already selected
  useEffect(() => {
    if (values.jobListingId && !jobListingsResponse && !jobListingsLoading) {
      getJobListings()
    }
  }, [])

  // Update local state when app values change
  useEffect(() => {
    setValues({ ...appValues[0] })
  }, [appValues])

  const handleEdit = () => {
    setEditing(true)
  }

  const handleCancel = () => {
    setValues({ ...appValues[0] })
    setEditing(false)
  }

  const isSaveDisabled = () => {
    return !values.jobListingId || !values.trackingLinkId
  }

  const handleSave = async () => {
    if (isSaveDisabled()) return

    setLoading(true)
    try {
      const updates = {
        ...(values.availableForWork !== undefined && {
          availableForWork: values.availableForWork,
        }),
        ...(values.jobListingId !== undefined && {
          jobListingId: values.jobListingId,
        }),
        ...(values.trackingLinkId !== undefined && {
          trackingLinkId: values.trackingLinkId,
        }),
      }

      const result = await handleUpdateApplication(updates)
      if (!result.errors) {
        setEditing(false)
      }
    } catch (error) {
      // Error handled by mutation
    } finally {
      setLoading(false)
    }
  }

  // Get selected labels for tooltips
  const selectedJobListingLabel =
    jobListingOptions.find((option) => option.value === values.jobListingId)
      ?.label || ''

  const trackingLinkOptions = calculateTrackingLinks(values.jobListingId)

  // If the applicant's tracking link belongs to a different job listing
  // (e.g. job listing was changed after application), add it as a fallback
  // option so the Select can still display it.
  if (
    values.trackingLinkId &&
    trackingLinkOptions &&
    !trackingLinkOptions.find(
      (option) => option.value === values.trackingLinkId
    ) &&
    values.linkType
  ) {
    const fallbackLabel = `${LinkTypeDisplay[values.linkType] || values.linkType}${
      values.linkName ? `: ${values.linkName}` : ''
    }`
    trackingLinkOptions.push({
      value: values.trackingLinkId,
      label: fallbackLabel,
    })
  }

  const selectedTrackingLinkLabel =
    trackingLinkOptions?.find(
      (option) => option.value === values.trackingLinkId
    )?.label || ''

  return (
    <div
      className={`flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3`}
    >
      <div
        className="text-md flex w-full cursor-pointer flex-row items-center gap-2 font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        Job Information
        {!editing ? (
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              if (!opened) setOpened(true)
              handleEdit()
            }}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
            Edit
          </DNButton>
        ) : (
          <div className="ml-auto flex items-center gap-2">
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleSave()
              }}
              disabled={loading || isSaveDisabled()}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleCancel()
              }}
              disabled={loading}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
            {loading && <Loader size={12} color="gray" />}
          </div>
        )}
      </div>
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-2">
          <div className={`grid grid-cols-12 gap-3`}>
        <div className="col-span-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-doubleNickel-gray-700">
              Job Listing
              {editing && <span className="text-red-500"> *</span>}
            </label>
            <Tooltip
              label={selectedJobListingLabel}
              disabled={editing || !values.jobListingId}
              withArrow
              multiline
              maw={400}
            >
              <div>
                <SelectInput
                  editing={editing}
                  value={values.jobListingId}
                  rightSection={
                    editing && jobListingsLoading ? <Loader size="1rem" /> : null
                  }
                  data={jobListingOptions}
                  onDropdownOpen={() => {
                    if (!jobListingsResponse) {
                      getJobListings()
                    }
                  }}
                  handleChange={(value) => {
                    setValues({
                      ...values,
                      jobListingId: value,
                      trackingLinkId: null,
                    })
                  }}
                />
              </div>
            </Tooltip>
          </div>
        </div>
        <div className="col-span-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-doubleNickel-gray-700">
              Tracking Link
              {editing && <span className="text-red-500"> *</span>}
            </label>
            <Tooltip
              label={selectedTrackingLinkLabel}
              disabled={editing || !values.trackingLinkId}
              withArrow
              multiline
              maw={400}
            >
              <div>
                <SelectInput
                  editing={editing}
                  value={values.trackingLinkId}
                  data={trackingLinkOptions}
                  handleChange={(value) => {
                    setValues({ ...values, trackingLinkId: value })
                  }}
                />
              </div>
            </Tooltip>
          </div>
        </div>
        <div className="col-span-2">
          <DateInput
            label={
              <div className="flex items-center gap-1">
                <span>Applied On</span>
                <Tooltip label="Date when the applicant submitted their application">
                  <div className="flex items-center">
                    <IconInfoCircle className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
                  </div>
                </Tooltip>
              </div>
            }
            editing={false}
            value={values.applicationDate}
          />
        </div>
        <div className="col-span-2">
          <DateInput
            label={
              <div className="flex items-center gap-1">
                <span>Available On</span>
                <Tooltip label="Date when the applicant is available to start work">
                  <div className="flex items-center">
                    <IconInfoCircle className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
                  </div>
                </Tooltip>
              </div>
            }
            editing={editing}
            value={values.availableForWork}
            onChange={(value) => {
              setValues({ ...values, availableForWork: value })
            }}
          />
        </div>
      </div>
    </div>
  </Collapse>
    </div>
  )
}

export default JobInformationCard
